/**
 * FX-CI-001a — Currency Identity Context (low-level, READ-ONLY).
 *
 * Exposes the Four-Layer currency *identity* + provenance for app-wide consumers.
 * IDENTITY ONLY — never converts amounts, never formats numbers, never writes.
 *
 * Dependency order (acyclic — Reservation 3):
 *   Auth/tenant data → api (axios) → CurrencyIdentity → LanguageContext / formatters / presentation.
 * This module imports ONLY `api` + React. It does NOT import LanguageContext, fmt, ti, or any
 * formatter that will later consume currency identity, so no provider cycle can form.
 *
 * Authority (Reservation 2): all four reads use `require_user` endpoints (any authenticated
 * member). Reads degrade PER LAYER — one failed/forbidden source never breaks the others and
 * never fabricates a value. Mutations (operating-currency / rate policy) are intentionally
 * NOT exposed here; owner-only writes live in useFinancialConfig for the Settings surface.
 */
import { createContext, useContext } from "react";
import { useFinancialConfig } from "../lib/useFinancialConfig";

const CurrencyIdentityContext = createContext(null);

export function CurrencyIdentityProvider({ children }) {
  // useFinancialConfig fetches L1/L2/L4 (+ L3 settlement) via require_user endpoints and
  // already degrades per-layer (per-source loading/error). We expose ONLY read identity here.
  const { operating, engine, policy, settlement } = useFinancialConfig();

  const value = {
    operatingCurrency: operating.data?.operating_currency || null,
    operatingCurrencyProvenance: operating.data?.provenance || null,
    operatingCurrencyIsFallback:
      !!operating.data && operating.data.provenance !== "tenant_configured",
    engineCurrency: engine.data?.currency || "USD", // L2 system constant; safe non-fallback
    settlement, // { available, currency, ... } | { available:false, reason }
    exchangeRatePolicy: policy.data?.policy || null,
    loading: operating.loading || engine.loading,
    errors: {
      operating: operating.error,
      engine: engine.error,
      policy: policy.error,
      settlement: settlement?.reason === "unavailable",
    },
  };

  return (
    <CurrencyIdentityContext.Provider value={value}>
      {children}
    </CurrencyIdentityContext.Provider>
  );
}

/** Read the Four-Layer currency identity. Safe to call anywhere below the provider. */
export function useCurrencyIdentity() {
  const ctx = useContext(CurrencyIdentityContext);
  // Graceful default when used outside the provider — identity unknown, never fabricated.
  if (!ctx) {
    return {
      operatingCurrency: null, operatingCurrencyProvenance: null, operatingCurrencyIsFallback: false,
      engineCurrency: "USD", settlement: { available: false, reason: "unavailable" },
      exchangeRatePolicy: null, loading: false, errors: {},
    };
  }
  return ctx;
}
